import React, { useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { verifyAuth } from "../../authverify";
import { Sidebar } from "../Sidebar/Sidebar";
import { GridDashBoard } from "../../style/Dashboard";
import { Navbar } from "../Navbar/Navbar";
import MobileNavbar from "../MobileNav/MobileNav";
import CustomizePopup from "../Home/CustomizePopup";
import ReviewModal from "./ReviewModal";

import Cal from "../../assets/sidebar/calendar.svg";
import User from "../../assets/sidebar/user.svg";
import Menu from "../../assets/sidebar/menu.svg";
import Person from "../../assets/sidebar/person.svg";
import CalA from "../../assets/sidebar/calendarA.svg";
import UserA from "../../assets/sidebar/userA.svg";
import MenuA from "../../assets/sidebar/menuA.svg";
import PersonA from "../../assets/sidebar/personA.svg";
import TaskIcon from "../../assets/sidebar/tasklist.svg";
import TaskIconA from "../../assets/sidebar/tasklistA.svg";

import "./TaskList.css";

const TASKS = [
  {
    id: 1,
    patientName: "Jane Smith",
    medication: "Wegovy 0.25 mg",
    status: "READY FOR REVIEW",
    statusType: "action_required",
    updatedAt: "Updated 25 min ago",
  },
  {
    id: 2,
    patientName: "Robert Chen",
    medication: "Humira 40 mg",
    status: "REQUESTED",
    statusType: "requested",
    updatedAt: "Updated 1h ago",
  },
  {
    id: 3,
    patientName: "Maria Garcia",
    medication: "Ozempic 1 mg",
    status: "SUBMITTED",
    statusType: "submitted",
    updatedAt: "Updated Yesterday",
  },
  {
    id: 4,
    patientName: "James Wilson",
    medication: "Skyrizi 150 mg",
    status: "APPROVED",
    statusType: "approved_denied",
    updatedAt: "Updated 2 days ago",
  },
  {
    id: 5,
    patientName: "Lisa Park",
    medication: "Mounjaro 5 mg",
    status: "DENIED",
    statusType: "approved_denied",
    updatedAt: "Updated 3 days ago",
  },
];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "action_required", label: "Action required" },
  { key: "submitted", label: "Submitted" },
  { key: "approved_denied", label: "Approved / Denied" },
];

const STATUS_CLASS_MAP = {
  "READY FOR REVIEW": "status-ready",
  REQUESTED: "status-requested",
  SUBMITTED: "status-submitted",
  APPROVED: "status-approved",
  DENIED: "status-denied",
};

const TaskList = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [tasks, setTasks] = useState(TASKS);
  const history = useHistory();
  const location = { pathname: "/TaskList" };

  const handleOpenReview = (task) => {
    setSelectedTask(task);
    setReviewOpen(true);
  };

  const handleSubmitTask = (taskId) => {
    setTasks((prev) =>
      prev.map((t) =>
        t.id === taskId
          ? {
              ...t,
              status: "SUBMITTED",
              statusType: "submitted",
              updatedAt: "Updated just now",
            }
          : t
      )
    );
    setReviewOpen(false);
  };

  if (!verifyAuth()) {
    history.push({ pathname: "/" });
  }

  const readyForReviewCount = useMemo(
    () => tasks.filter((t) => t.status === "READY FOR REVIEW").length,
    [tasks]
  );

  const sidebarConfig = useMemo(
    () => [
      { path: "/Calender", icon: Cal, activeIcon: CalA, alt: "Cal", label: "Calendar" },
      { path: "/People/dashboard", icon: User, activeIcon: UserA, alt: "User", label: "People" },
      { path: "", icon: Menu, activeIcon: MenuA, alt: "Menu", label: "Menu" },
      { path: "", icon: Person, activeIcon: PersonA, alt: "Person", label: "Person" },
      {
        path: "/TaskList",
        icon: TaskIcon,
        activeIcon: TaskIconA,
        alt: "TaskList",
        label: "Tasks",
        badge: readyForReviewCount,
      },
    ],
    [readyForReviewCount]
  );

  const filteredTasks = useMemo(() => {
    let result = tasks;
    if (activeFilter !== "all") {
      result = result.filter((t) => t.statusType === activeFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.patientName.toLowerCase().includes(q) ||
          t.medication.toLowerCase().includes(q)
      );
    }
    return result;
  }, [tasks, activeFilter, searchQuery]);

  const getBorderColor = (status) => {
    switch (status) {
      case "READY FOR REVIEW": return "#F5A623";
      case "REQUESTED": return "#999";
      case "SUBMITTED": return "#2196F3";
      case "APPROVED": return "#4CAF50";
      case "DENIED": return "#F44336";
      default: return "#ccc";
    }
  };

  return (
    <>
      <MobileNavbar />
      <GridDashBoard>
        <div className="desk-visibility">
          <Sidebar config={sidebarConfig} />
          <div className="desk-navbar-wrap">
            <div className="conatiner">
              <Navbar Heading="Task List" handleSettingClick={() => setOpen(true)} />
            </div>
          </div>
        </div>
        <div className="customeGridArea">
          <div className="conatiner">
            <div className="task-list-content">
              <div className="task-filters">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    className={`filter-btn ${activeFilter === f.key ? "filter-active" : ""}`}
                    onClick={() => setActiveFilter(f.key)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              <div className="task-cards">
                {filteredTasks.map((task) => (
                  <div
                    key={task.id}
                    className="task-card"
                    style={{ borderLeftColor: getBorderColor(task.status) }}
                  >
                    <div className="task-card-info">
                      <div className="task-patient-name">{task.patientName}</div>
                      <div className="task-medication">{task.medication}</div>
                      <span className={`task-status-badge ${STATUS_CLASS_MAP[task.status]}`}>
                        {task.status}
                      </span>
                      <div className="task-updated">{task.updatedAt}</div>
                    </div>
                    <div className="task-card-action">
                      {task.status === "READY FOR REVIEW" ? (
                        <button
                          className="task-action-btn task-btn-primary"
                          onClick={() => handleOpenReview(task)}
                        >
                          Review &amp; submit
                        </button>
                      ) : (
                        <button
                          className="task-action-btn task-btn-outline"
                          onClick={() => handleOpenReview(task)}
                        >
                          View
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {filteredTasks.length === 0 && (
                  <div className="task-empty">No tasks match the selected filter.</div>
                )}
              </div>
            </div>
          </div>
        </div>
        {open && <CustomizePopup open={open} onClose={() => setOpen(false)} />}
        <ReviewModal
          open={reviewOpen}
          onClose={() => setReviewOpen(false)}
          task={selectedTask}
          onSubmit={() => selectedTask && handleSubmitTask(selectedTask.id)}
        />
      </GridDashBoard>
    </>
  );
};

export default TaskList;
